import { useState, useEffect, useRef, useCallback } from 'react';
import { doWebTimestampCheck } from '../sdk';

const usePrevious = (value: any): any => {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const useStateCallback = (initialState: any): any => {
  const [state, setState] = useState(initialState);
  const cbRef = useRef(null);

  const setStateCallback = useCallback((newState, cb) => {
    cbRef.current = cb;
    setState(newState);
  }, []);

  useEffect(() => {
    if (cbRef.current) {
      cbRef.current(state);
      cbRef.current = null;
    }
  }, [state]);

  return [state, setStateCallback];
};

const useComponentWillMount = (func: any): void => {
  const willMount = useRef(true);

  if (willMount.current) func();

  willMount.current = false;
};

const useComponentDidUpdate = (callback: any, deps: any): void => {
  const hasMount = useRef(false);

  useEffect(() => {
    if (hasMount.current) {
      callback();
    } else {
      hasMount.current = true;
    }
  }, [deps, callback]);
};

type UseForceReload = () => {
  isLoading: boolean;
};

const useWebTimestampCheck: UseForceReload = () => {
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleForceReload = async () => {
      setIsLoading(true);

      try {
        const response = await doWebTimestampCheck();
        const isForceReloaded = localStorage.getItem('forceReload');

        if (response.data.shouldReloadWebApp && !isForceReloaded) {
          localStorage.setItem('forceReload', 'true');
          window.location.reload();
          return;
        }

        localStorage.removeItem('forceReload');
      } catch (err) {
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    };

    handleForceReload();
  }, []);

  return { isLoading };
};

type UseCameraReady = () => {
  permissionStatus: string;
};

const useCameraReady: UseCameraReady = () => {
  const [permissionStatus, setPermissionStatus] = useState('');

  useEffect(() => {
    // Function to check camera permission
    const checkCameraPermission = async () => {
      try {
        // Get list of all media devices (camera, microphone, etc.)
        const devices = await navigator.mediaDevices.enumerateDevices();

        // Check if any of the devices is a video input (camera)
        const videoDevices = devices.filter(device => device.kind === 'videoinput');

        if (videoDevices.length === 0) {
          setPermissionStatus('NO_CAMERA_FOUND');
          return;
        }

        // Attempt to access the camera
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });

        // If the above line succeeds, permission is granted
        stream.getTracks().forEach(track => track.stop()); // Stop the stream once we are done checking
        setPermissionStatus('GRANTED');
      } catch (error) {
        if (error.name === 'NotAllowedError') {
          setPermissionStatus('PERMISSION_DENINED');
        } else if (error.name === 'NotFoundError') {
          setPermissionStatus('NO_CAMERA_FOUND');
        } else {
          setPermissionStatus('NO_CAMERA_FOUND');
        }
      }
    };

    checkCameraPermission();
  }, []);

  return { permissionStatus };
};

export {
  usePrevious,
  useStateCallback,
  useComponentWillMount,
  useComponentDidUpdate,
  useWebTimestampCheck,
  useCameraReady,
};
